<script setup lang="ts">
import {
  computed,
  CSSProperties,
  nextTick,
  onBeforeUnmount,
  type PropType,
  reactive,
  ref,
  SetupContext,
  useSlots,
  watch,
} from "vue";
import {
  ControlSize,
  DesignVersion,
  ExtendedDesignVersion,
  Placement,
  PositioningStrategy,
  Point,
} from "@/models/types";
import {
  calculateArrowPosition,
  determineTooltipPositionAndPlacement,
  determineTooltipPositionFromMousePosition,
} from "@/models/tooltip-positioning";

defineOptions({ name: "BcTooltip" });

const slots: SetupContext["slots"] = useSlots();

const props = defineProps({
  /** Adds arrow to the tooltip */
  arrow: { type: Boolean, default: true },
  /** Automatically sets the position of the tooltip */
  autoPlacement: { type: Boolean, default: true },
  /** Specifies the tooltip content text */
  content: { type: String },
  /** Specifies the tooltip content's style */
  contentDesign: {
    type: String as PropType<ExtendedDesignVersion>,
    default: "dark",
    validator: (value: any) => ExtendedDesignVersion.isExtendedDesignVersion(value),
  },
  /** Disables the tooltip */
  disabled: { type: Boolean, default: false },
  /**
   * Specifies the distance of the popup from its reference element.
   * @type {[number, number]}
   */
  offset: {
    type: Array as PropType<Array<number>>,
    validator: (value: any) =>
      Array.isArray(value) &&
      value.length === 2 &&
      value.every((v) => typeof v === "number"),
  },
  /** Specifies the placement of the tooltip compared to the wrapper element */
  placement: {
    type: String as PropType<Placement>,
    default: "top",
    validator: (value: any) => Placement.isPlacement(value),
  },
  /**
   * Specifies the popup positioning strategy.
   * @type {PositioningStrategy}
   * @default ['fixed']
   */
  positioningStrategy: {
    type: String as PropType<PositioningStrategy>,
    default: "fixed",
    validator: (value: any) => PositioningStrategy.isPositioningStrategy(value),
  },
  /** Specifies the tooltip trigger's style (if svg icon used) */
  triggerDesign: {
    type: String as PropType<DesignVersion>,
    default: "6",
    validator: (value: any) => DesignVersion.isDesignVersion(value),
  },
  /** Makes tooltip content's border rounded */
  rounded: { type: Boolean, default: true },
  /** Specifies pre-defined sizes for the tooltip content */
  size: {
    type: String as PropType<ControlSize>,
    validator: (value: any) => ControlSize.isControlSize(value),
  },
  /** Shows tooltip at mouse position */
  mouseMode: {
    type: Boolean,
    default: false,
  },
  /** Time before tooltip is shown in mouse mode */
  mouseModeDebounceTime: {
    type: Number,
    default: 500,
  },
  /** Sets the target for the tooltip teleport element (default: body) */
  teleportTarget: {
    type: String,
    default: "body",
  },
});

/**
 * Element refs
 */

const tooltipControlRef = ref<HTMLDivElement | null>(null);
const triggerRef = ref<HTMLDivElement | null>(null);
const tooltipRef = ref<HTMLDivElement | null>(null);
const arrowRef = ref<HTMLDivElement | null>(null);

/**
 * Element styles
 */

const tooltipStyles = reactive<CSSProperties>({
  position: props.positioningStrategy,
  top: 0,
  left: 0,
});

const arrowStyles = reactive<CSSProperties>({
  position: "absolute",
  top: 0,
  left: 0,
});

/**
 * Tooltip
 */

const isVisible = ref<boolean>(false);
let mouseTimer: any;
let hasEntered: boolean = false;

const offset = ref(props.offset);
const arrow = ref(props.arrow);
const rounded = ref(props.rounded);

const pak = computed<string>(() => {
  const design =
    props.contentDesign === "mouseMode" || props.mouseMode ? "7" : props.triggerDesign;
  return "bc-controls-d" + design;
});

if (props.contentDesign === "mouseMode") {
  offset.value = [8, -8];
  arrow.value = false;
  rounded.value = true;
}

function showTooltip() {
  if (props.disabled || (!props.content && !slots.content)) {
    return;
  }
  isVisible.value = true;

  window.addEventListener("scroll", onScroll);
  window.addEventListener("resize", onResize);

  // the tooltip should already be visible when updateTooltip is called,
  // otherwise it has no width and height
  nextTick(() => {
    if (!props.mouseMode) {
      updateTooltip();
    }
    if (tooltipRef.value) {
      mutationObserver.observe(tooltipRef.value, { childList: true, subtree: true });
    }
  });
}

function hideTooltip() {
  isVisible.value = false;

  window.removeEventListener("scroll", onScroll);
  window.removeEventListener("resize", onResize);

  mutationObserver.disconnect();

  updateTooltip();
}

function updateTooltip() {
  if (!triggerRef.value || !tooltipRef.value) return;

  if (!isVisible.value) {
    tooltipStyles.transform = undefined;
    return;
  }

  const arrowElement = (arrow && arrowRef.value) || null;

  // Apply styles to the tooltip
  tooltipStyles.font = tooltipControlRef.value
    ? getComputedStyle(tooltipControlRef.value, null).getPropertyValue("font")
    : undefined;

  nextTick(() => {
    // Calculate the initial position of the tooltip
    let { position, placement } = determineTooltipPositionAndPlacement(
      triggerRef.value!,
      tooltipRef.value!,
      arrowElement,
      props.placement,
      offset.value
    );

    // Apply the calculated position to the tooltip
    tooltipStyles.transform = `translate(${position.x}px, ${position.y}px)`;

    // If not has arrow return
    if (!arrowElement) return;

    // Calculate the arrow's position relative to the tooltip
    const arrowPosition = calculateArrowPosition(
      triggerRef.value!,
      tooltipRef.value!,
      arrowElement,
      placement,
      position
    );
    if (arrowPosition) {
      // Apply styles and the calculated position to the arrow
      arrowStyles.transform = `translate(${arrowPosition.x}px, ${arrowPosition.y}px)`;
    }
  });
}

function updateTooltipByMousePosition(mousePosition: Point) {
  if (!tooltipRef.value) return;

  // Calculate position
  const position = determineTooltipPositionFromMousePosition(
    tooltipRef.value,
    mousePosition,
    offset.value
  );

  // Apply styles
  tooltipStyles.transform = `translate(${position.x}px, ${position.y}px)`;
  tooltipStyles.font = tooltipControlRef.value
    ? getComputedStyle(tooltipControlRef.value, null).getPropertyValue("font")
    : undefined;
  arrowStyles.transform = undefined;
}

/**
 * EventHandler
 */

const onClick = () => {
  if (!props.mouseMode) return;
  onLeave();
};

const onEnter = () => {
  if (props.mouseMode) {
    hasEntered = true;
    return;
  }
  showTooltip();
};

const onLeave = () => {
  if (mouseTimer) clearTimeout(mouseTimer);
  hasEntered = false;
  hideTooltip();
};

const onMove = (e: MouseEvent) => {
  if (!props.mouseMode || !hasEntered) return;

  // reset mouse timer
  if (mouseTimer) clearTimeout(mouseTimer);

  // start timer to show if not visible
  if (!isVisible.value) {
    mouseTimer = setTimeout(() => {
      showTooltip();
      nextTick(() => updateTooltipByMousePosition({ x: e.clientX, y: e.clientY }));
    }, props.mouseModeDebounceTime);
  }
};

function onContentChanged() {
  if (!isVisible.value) return;
  updateTooltip();
}

function onDisabledChanged(disabled: boolean) {
  if (!isVisible.value || !disabled) return;
  hideTooltip();
}

function onScroll() {
  hideTooltip();
}

function onResize() {
  hideTooltip();
}

/**
 * Watcher / Observer
 */

watch(() => props.content, onContentChanged); // only observes the property not the slot (slot see below)
watch(() => props.disabled, onDisabledChanged);

// observer for detecting changes in the content slot
const mutationObserver = new MutationObserver(() => onContentChanged());

/**
 * Lifecycle
 */

onBeforeUnmount(() => {
  hideTooltip();
});

/**
 * Expose
 */

defineExpose({
  tooltipRef,
  triggerRef,
  arrowRef,
  tooltipStyles,
  arrowStyles,
  isVisible,
  offset,
  arrow,
  rounded,
  onClick,
  onEnter,
  onLeave,
  onMove,
});
</script>

<template>
  <div :data-pak="pak" class="bissantzControls tooltipControl" ref="tooltipControlRef">
    <div
      ref="triggerRef"
      class="trigger"
      :class="{ disabled }"
      :data-design="triggerDesign"
      @click="onClick"
      @contextmenu="onClick"
      @mouseenter="onEnter"
      @mousemove="onMove"
      @mouseleave="onLeave"
    >
      <slot>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          width="16.25px"
          height="16.25px"
          version="1.2"
          baseProfile="tiny"
          id="Ebene_1"
          x="0px"
          y="0px"
          viewBox="0 0 379.836 379.836"
          overflow="visible"
          xml:space="preserve"
        >
          <rect x="166.148" y="147.295" width="47.541" height="163.934" />
          <rect x="166.148" y="75.41" width="47.541" height="47.541" />
          <circle
            class="circle"
            fill="none"
            stroke-width="20"
            stroke-miterlimit="10"
            cx="189.918"
            cy="189.918"
            r="179.918"
          />
        </svg>
      </slot>
    </div>
  </div>

  <Teleport v-bind:to="teleportTarget">
    <div
      ref="tooltipRef"
      class="bissantzControls tooltipControl tooltip"
      role="tooltip"
      v-show="isVisible"
      :data-design="contentDesign"
      :data-locid="$attrs['data-content-locid']"
      :data-locparam="$attrs['data-label-locparam']"
      :data-locparam1="$attrs['data-label-locparam1']"
      :data-locparam2="$attrs['data-label-locparam2']"
      :data-pak="pak"
      :class="[size ? `content-${size}` : '', { rounded }]"
      :style="tooltipStyles"
    >
      <template v-if="content">{{ content }}</template>
      <template v-else-if="$slots.content">
        <slot name="content" />
      </template>
      <div
        v-if="arrow && !mouseMode && (content || $slots.content)"
        ref="arrowRef"
        class="arrow"
        :style="arrowStyles"
      ></div>
    </div>
  </Teleport>
</template>

<style lang="less">
@import "../styles/components/bc-tooltip.less";
</style>
